import { createAsyncThunk } from '@reduxjs/toolkit';
import { isValidDashboardLayout } from 'scenes/Project/components/ProjectOverview/modules/dashboardLayout';
import { selectUserActiveEmployerInfo } from 'store/user/selectors.js/user.selectors';
import { patchProjectDashboardLayout } from '../services/patchProjectDashboardLayout.service';

/**
 * The dashboard's module catalogue, for the modules' sizes. Loaded when a
 * layout is saved rather than with the store: the catalogue brings the
 * dashboard's sections (and the sensor gauges' library) with it, which the
 * main bundle need not carry. The dashboard has loaded it already by then.
 * @returns {Promise<ReadonlyArray<Object>>}
 */
const loadDashboardModules = async () =>
  (await import('scenes/Project/components/ProjectOverview/modules/registry'))
    .default;

/**
 * Saves the project's dashboard layout, `{ version: 1, items }`, and gives
 * `{ projectId, dashboardLayout, updatedAt }` as the API saved them (the
 * layout normalized).
 *
 * The layout is checked first as the API checks it (isValidDashboardLayout
 * against the module catalogue: its shape, known modules, an item's `h` only
 * on a resizable module and within its range, footprints of that height on
 * the grid, no module twice, no cell covered twice); one that fails is never
 * sent.
 *
 * Rejected with `{ status, message }`: the API's HTTP status (403: no
 * PROJECT_EDIT in the project, 404: no such project, or an API without the
 * endpoint, 400: a layout the API refuses), or `status: null` when no answer
 * came, or when the layout was never sent because it fails the check.
 */
export const saveProjectDashboardLayout = createAsyncThunk(
  'projects/saveProjectDashboardLayout',
  async ({ projectId, dashboardLayout }, { getState, rejectWithValue }) => {
    if (!projectId) {
      return rejectWithValue({ status: null, message: 'No project id' });
    }

    let modules;
    try {
      modules = await loadDashboardModules();
    } catch (error) {
      return rejectWithValue({
        status: null,
        message: error?.message || 'Loading the dashboard modules failed',
      });
    }

    if (!isValidDashboardLayout(dashboardLayout, modules)) {
      return rejectWithValue({
        status: null,
        message: 'Invalid dashboard layout',
      });
    }

    try {
      const updatedProject = await patchProjectDashboardLayout({
        projectId,
        dashboardLayout,
        activeEmployerInfo: selectUserActiveEmployerInfo(getState()),
      });

      return {
        projectId: updatedProject?.id || projectId,
        // The layout as sent when the answer has none: the API took it.
        dashboardLayout: updatedProject?.dashboardLayout || dashboardLayout,
        updatedAt: updatedProject?.updatedAt,
      };
    } catch (error) {
      const data = error?.response?.data;
      const message =
        (typeof data === 'string' && data) ||
        data?.message ||
        data?.error ||
        error?.message ||
        'Saving the dashboard layout failed';

      return rejectWithValue({
        status: error?.response?.status ?? null,
        message,
      });
    }
  }
);
